import { useFormikContext } from 'formik';
import React, { useContext } from 'react';

import styled from '@emotion/styled';

import useReUploadDriverInfo from '../../../../hooks/dashboard/driver-list/useReUploadDriverInfo';
import {
  ReUploadDriverInfoDataType,
  ReUploadDriverInfoInitialValues,
  ReUploadDriverInfoSchema,
} from '../../../../models/dashboard/driver-list/reupload-driver-info';
import { FormSubmitButton } from '../../../Button/FormSubmitButton';
import { FormComponent } from '../../../FormCom/FormComponent';
import { TextInputField } from '../../../FormCom/FormField';
import ImageUploadField from '../../../FormCom/ImageUploadField';
import NumberOnlyInputField from '../../../FormCom/NumberOnlyInputField';
import ModalContainer from '../../ModalContainer';
import { ModalContext } from '../../ModalContext';

interface ReUploadInfoModalProps {
  data: any;
}

interface ReUploadInfoFormBodyProps {
  data: any;
  loading: boolean;
}

const ReUploadInfoFormBody: React.FC<ReUploadInfoFormBodyProps> = ({
  data,
  loading,
}) => {
  const { values } = useFormikContext<ReUploadDriverInfoDataType>();
  const modalContext = useContext(ModalContext);

  const fullName = [values.firstName, values.lastName]
    .map((n) => n?.trim())
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <BioArea>
        <ImageUploadField
          id='profilePicture'
          name='profilePicture'
          defaultImgUrl={
            data?.profilePicture ??
            '/static/img/profilePlaceholderLargePlain.png'
          }
          setDefaultImgUrl={() => {}}
          title={fullName || data?.name || 'Driver Name'}
          showSupportText={false}
        />
      </BioArea>

      <TextInputField
        name='firstName'
        id='firstName'
        type='text'
        label='First Name'
        placeholder='Enter the driver first name'
      />

      <TextInputField
        name='lastName'
        id='lastName'
        type='text'
        label='Last Name'
        placeholder='Enter the driver last name'
      />

      <NumberOnlyInputField
        name='nin'
        id='nin'
        type='text'
        label='National Identity Number (NIN) (Required)'
        placeholder='Enter the driver NIN'
        maxLength={11}
      />

      <TextInputField
        name='dateOfBirth'
        id='dateOfBirth'
        type='date'
        label='Date of Birth'
        placeholder='Enter the driver date of birth'
      />

      <ButtonArea>
        <FormSubmitButton
          className='clear fill'
          btnType='button'
          onClick={() => modalContext?.setShowModal(false)}
        >
          Cancel
        </FormSubmitButton>
        <FormSubmitButton loading={loading} className='fill'>
          Resubmit
        </FormSubmitButton>
      </ButtonArea>
    </>
  );
};

const ReUploadInfoModal: React.FC<ReUploadInfoModalProps> = ({ data }) => {
  const modalContext = useContext(ModalContext);
  const { reUploadDriverInfo, loading } = useReUploadDriverInfo();

  const onSubmit = (values: ReUploadDriverInfoDataType) => {
    reUploadDriverInfo(values, data?.driverId ?? '', () =>
      modalContext?.setShowModal(false),
    );
  };

  const initialValues: ReUploadDriverInfoDataType = {
    ...ReUploadDriverInfoInitialValues,
    firstName: data?.firstName ?? ReUploadDriverInfoInitialValues.firstName,
    lastName: data?.lastName ?? ReUploadDriverInfoInitialValues.lastName,
    dateOfBirth:
      data?.dateOfBirth?.slice(0, 10) ??
      ReUploadDriverInfoInitialValues.dateOfBirth,
  };

  return (
    <ModalContainer label='Reupload Driver Details'>
      <FormComponent
        initialValues={initialValues}
        schema={ReUploadDriverInfoSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        <ReUploadInfoFormBody data={data} loading={loading} />
      </FormComponent>
    </ModalContainer>
  );
};

export default ReUploadInfoModal;

const ButtonArea = styled.div`
  display: flex;
  width: 100%;
  gap: 16px;
`;

const BioArea = styled.div`
  border: 1px solid red;
  display: flex;
  gap: 20px;
  align-items: center;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;
`;
